<template>
  <CustomModal
    title="Insert Image"
    :visible="modelValue"
    @update:visible="$emit('update:modelValue')"
    :width="'32vw'"
  >
    <div id="image_insert_modal_body" class="flex flex-col gap-4">
      <div id="image_insert_modal_upload_image" class="flex flex-col gap-4">
        <div
          id="image_insert_modal_upload_component"
          class="flex flex-col gap-2"
        >
          <span class="text-[13px] secondary_color">Upload Image</span>
          <UploadComponent
            source="image_insert_modal"
            type="image"
            :model-value="uploadedFile"
            @update:model-value="handleFileUpload"
            description="Supported formats: JPG, JPEG, PNG, GIF, WEBP (Max: 5MB)"
          />
        </div>

        <UploadedFileRenderer
          source="image_insert_modal"
          :files="uploadedFiles"
          action-text="Remove"
          @action="handleRemoveFile"
        />
      </div>

      <div class="flex items-center gap-2">
        <div class="flex-1 h-[1px] bg-[var(--white-opacity-20)]"></div>
        <span class="text-xs secondary_color">OR</span>
        <div class="flex-1 h-[1px] bg-[var(--white-opacity-20)]"></div>
      </div>

      <div id="image_insert_modal_image_url" class="flex flex-col gap-2">
        <span class="text-[13px] secondary_color">Image URL</span>
        <CustomInput
          format="text"
          type="primary"
          :model-value="imageUrl"
          placeholder="Enter Image URL"
          :is-error="!!imageUrlError"
          :error-message="imageUrlError"
          @update:model-value="handleImageUrlUpdate"
        />
      </div>
    </div>

    <template #footer>
      <div id="image_insert_modal_footer" class="flex justify-end gap-2">
        <a-button @click="handleCancel" class="secondary_medium"
          >Cancel</a-button
        >
        <a-button
          class="primary_medium"
          @click="handleConfirm"
          :disabled="insertButtonDisabled"
          :loading="isUploading"
        >
          {{ isUploading ? "Uploading.." : "Insert" }}
        </a-button>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { computed, ref } from "vue";
import { message } from "ant-design-vue";
import CustomModal from "@/src/components/CustomModal.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import DashboardService from "@/src/api/DashboardService.js";
import UploadComponent from "@/src/components/upload_components/UploadComponent.vue";
import UploadedFileRenderer from "@/src/components/upload_components/UploadedFileRenderer.vue";

const props = defineProps({
  modelValue: { type: Boolean, required: true },
  imageUrl: { type: String, default: "" },
  imageUrlError: { type: String, default: "" },
});

const emit = defineEmits([
  "update:modelValue",
  "update:imageUrl",
  "confirm",
  "cancel",
]);

const imageUrl = ref(props.imageUrl);
const uploadedFile = ref(null);
const uploadedFiles = ref([]);
const isUploading = ref(false);
const uploadedImageUrls = ref([]);

const insertButtonDisabled = computed(() => {
  return (
    (!imageUrl.value && uploadedImageUrls.value.length === 0) ||
    isUploading.value
  );
});

const handleImageUrlUpdate = (value) => {
  try {
    imageUrl.value = value;
    emit("update:imageUrl", value);
  } catch (error) {
    console.error("An error occurred while handling image URL update: ", error);
  }
};

const handleRemoveFile = (index) => {
  try {
    uploadedFiles.value.splice(index, 1);
    uploadedImageUrls.value.splice(index, 1);
    if (uploadedFiles.value.length === 0) {
      uploadedFile.value = null;
      emit("update:imageUrl", "");
    }
  } catch (error) {
    console.error("An error occurred while handling remove: ", error);
  }
};

// Tries to fetch image from URL and upload to server.
// Falls back to direct URL if CORS or network blocks the fetch.
const fetchAndUploadUrl = async (url) => {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith("image/")) return null;
    const ext = blob.type.split("/")[1] || "png";
    const formData = new FormData();
    formData.append("files[]", blob, `image-from-url.${ext}`);
    const uploadRes = await DashboardService.uploadAttachment(formData);
    if (uploadRes?.success && uploadRes?.files?.[0]?.url) {
      return uploadRes.files[0].url;
    }
    return null;
  } catch {
    return null; // CORS / network error – caller falls back to direct URL
  }
};

const handleConfirm = async () => {
  try {
    if (uploadedImageUrls.value.length > 0) {
      uploadedImageUrls.value.forEach((url) => emit("confirm", url));
      handleCancel();
    } else if (imageUrl.value) {
      isUploading.value = true;
      const serverUrl = await fetchAndUploadUrl(imageUrl.value);
      emit("confirm", serverUrl || imageUrl.value);
      handleCancel();
    }
  } catch (error) {
    console.error("An error occurred while handling confirm: ", error);
  } finally {
    isUploading.value = false;
  }
};

const handleCancel = () => {
  try {
    uploadedFile.value = null;
    uploadedFiles.value = [];
    uploadedImageUrls.value = [];
    imageUrl.value = "";
    emit("update:imageUrl", "");
    emit("update:modelValue", false);
    emit("cancel");
  } catch (error) {
    console.error("An error occurred while handling cancel: ", error);
  }
};

const handleFileUpload = async (file) => {
  if (file) {
    uploadedFile.value = file;
    isUploading.value = true;

    const formData = new FormData();
    formData.append("files[]", file);

    try {
      const response = await DashboardService.uploadAttachment(formData);
      if (response.success && response.files && response.files.length > 0) {
        uploadedFiles.value.push(file);
        uploadedImageUrls.value.push(response.files[0].url);
        emit("update:imageUrl", response.files[0].url);
      } else {
        message.error("Failed to upload image");
        handleRemoveFile(uploadedFiles.value.length - 1);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      message.error("Failed to upload image");
      handleRemoveFile(uploadedFiles.value.length - 1);
    } finally {
      isUploading.value = false;
    }
  }
};
</script>
